// Reference data for the UDS (ISO 14229-1) NRC Decoder.
// Kept separate from the decode/parse logic so new codes, ranges, or SID names can be
// added here without touching parser.js or any component.
//
// Standardized NRC meanings, causes, and troubleshooting steps follow ISO 14229-1's
// generic server negative-response codes. Applicability of a given NRC to a given
// service, and the exact meaning of reserved/manufacturer-specific codes, can vary by
// ISO 14229-1 edition and by OEM diagnostic specification — see the in-app disclaimer.

export const hex2 = (n) => n.toString(16).toUpperCase().padStart(2, '0');

// Common UDS Service IDs, used only to label the "Original Service ID" byte in a
// decoded 7F/SID/NRC response. Not exhaustive of every OEM-defined service.
export const SID_NAMES = {
  0x10: 'Diagnostic Session Control',
  0x11: 'ECU Reset',
  0x14: 'Clear Diagnostic Information',
  0x19: 'Read DTC Information',
  0x20: 'Return To Normal',
  0x22: 'Read Data By Identifier',
  0x23: 'Read Memory By Address',
  0x24: 'Read Scaling Data By Identifier',
  0x27: 'Security Access',
  0x28: 'Communication Control',
  0x29: 'Authentication',
  0x2A: 'Read Data By Periodic Identifier',
  0x2C: 'Dynamically Define Data Identifier',
  0x2E: 'Write Data By Identifier',
  0x2F: 'Input Output Control By Identifier',
  0x31: 'Routine Control',
  0x34: 'Request Download',
  0x35: 'Request Upload',
  0x36: 'Transfer Data',
  0x37: 'Request Transfer Exit',
  0x38: 'Request File Transfer',
  0x3D: 'Write Memory By Address',
  0x3E: 'Tester Present',
  0x83: 'Access Timing Parameter',
  0x84: 'Secured Data Transmission',
  0x85: 'Control DTC Setting',
  0x86: 'Response On Event',
  0x87: 'Link Control',
};

export const getSidName = (sid) => SID_NAMES[sid] || null;

// Every entry below is a *standardized* ISO 14229-1 negative response code. `name` is
// the spec's camelCase identifier; `label` is a human-readable title-case version.
// `services` lists services this NRC is commonly associated with — it is a practical
// guide, not an exhaustive per-edition applicability matrix (see ISO 14229-1 Annex A
// for the authoritative per-service NRC applicability table).
export const NRC_DATABASE = {
  0x10: {
    name: 'generalReject', label: 'General Reject', category: 'General',
    meaning: 'A general, catch-all failure with no more specific NRC available.',
    description: 'The server (ECU) rejects the request, but none of the more specific negative response codes apply. It is a fallback used when the exact reason does not map to a defined NRC.',
    causes: [
      'ECU rejected the request for a reason not covered by a more specific NRC',
      'Used as a fallback by some ECU implementations for unhandled request errors',
    ],
    troubleshooting: [
      'Check ECU error/debug logs for the underlying cause',
      'Retry with a corrected or more specific request if possible',
      'Consult the ECU supplier for what triggers a generalReject in this implementation',
    ],
    services: ['Any service — this is a generic catch-all response'],
  },
  0x11: {
    name: 'serviceNotSupported', label: 'Service Not Supported', category: 'Request',
    meaning: 'The ECU does not implement this Service ID (SID) at all.',
    description: 'The requested diagnostic service is not supported by this ECU/software variant, independent of session or security state.',
    causes: [
      'SID not implemented by this ECU/software variant',
      'Wrong SID byte in the request (typo or tooling bug)',
      'ECU derivative does not include this service',
    ],
    troubleshooting: [
      'Verify the SID against this ECU\'s own diagnostic specification',
      'Confirm the ECU software variant/derivative supports this service',
      'Check for a typo in the tester/tool request',
    ],
    services: ['Applies at the SID level — any unsupported service'],
  },
  0x12: {
    name: 'subFunctionNotSupported', label: 'Sub-Function Not Supported', category: 'Request',
    meaning: 'The service exists, but this particular sub-function is not implemented.',
    description: 'The SID is recognized, but the sub-function byte (e.g. session type, reset type, control type) does not map to anything this ECU implements — again independent of session/security state.',
    causes: [
      'Sub-function value not implemented for this SID',
      'Sub-function valid on a different ECU/software variant but not this one',
      'Incorrect sub-function byte in the request',
    ],
    troubleshooting: [
      'Check the supported sub-functions for this SID in the ECU spec',
      'Verify the sub-function byte and suppressPosRspMsgIndicationBit encoding',
      'Confirm the request isn\'t using a sub-function reserved for a later spec revision',
    ],
    services: ['Any sub-function-based service, e.g. 0x10, 0x11, 0x27, 0x28, 0x3E, 0x85'],
  },
  0x13: {
    name: 'incorrectMessageLengthOrInvalidFormat', label: 'Incorrect Message Length Or Invalid Format', category: 'Length/Format',
    meaning: 'The request has the wrong number of bytes, or its structure is invalid for this service.',
    description: 'The length of the received message does not match what this service/sub-function expects, or the message does not otherwise conform to the required format (e.g. wrong field order, missing mandatory parameter).',
    causes: [
      'Missing or extra parameter bytes',
      'DID/RID length mismatch (e.g. 1 byte sent instead of 2)',
      'Malformed request structure (wrong field order)',
    ],
    troubleshooting: [
      'Compare the request byte count against the service\'s defined format',
      'Check DID/RID/parameter lengths are exactly as specified',
      'Verify lengths and sub-parameters in composite requests (e.g. WriteDataByIdentifier)',
    ],
    services: ['Any service — length/format is checked before other processing'],
  },
  0x14: {
    name: 'responseTooLong', label: 'Response Too Long', category: 'Length/Format',
    meaning: 'The response the ECU would send back exceeds the maximum length the transport supports.',
    description: 'The ECU accepted the request, but the resulting positive response would not fit within the transport protocol\'s (e.g. CAN, DoIP) maximum supported message length.',
    causes: [
      'Requested data (e.g. a DID or memory range) is larger than the transport can carry',
      'Multiple values requested together exceed the maximum response size',
    ],
    troubleshooting: [
      'Reduce the amount of data requested per call (fewer DIDs, smaller memory range)',
      'Check the transport\'s maximum payload/segmentation settings',
      'Split the request into multiple smaller requests',
    ],
    services: ['0x22 Read Data By Identifier', '0x23 Read Memory By Address', '0x2C Dynamically Define Data Identifier'],
  },
  0x21: {
    name: 'busyRepeatRequest', label: 'Busy — Repeat Request', category: 'Condition',
    meaning: 'The ECU is temporarily busy processing another task; repeat the request.',
    description: 'The server cannot process the request right now because it is busy, but the request itself is otherwise valid — the tester should resend it.',
    causes: [
      'ECU is completing a previous diagnostic or internal task',
      'A resource (e.g. NVM, bus) is temporarily locked',
    ],
    troubleshooting: [
      'Retry the exact same request after a short delay',
      'Check for overlapping/concurrent diagnostic requests from another tester',
      'Verify the ECU isn\'t stuck processing due to an unrelated fault',
    ],
    services: ['Any service'],
  },
  0x22: {
    name: 'conditionsNotCorrect', label: 'Conditions Not Correct', category: 'Condition',
    meaning: 'The ECU understood the request, but its current state does not allow it right now.',
    description: 'One or more preconditions required for this service are not currently met — this can be a session, security, or vehicle-state precondition not covered by a more specific NRC.',
    causes: [
      'Wrong diagnostic session active (e.g. request needs Extended/Programming session)',
      'Vehicle/engine state precondition not met (e.g. engine running, vehicle moving)',
      'Security access not unlocked at the required level',
      'Another diagnostic operation (e.g. flashing) already in progress',
    ],
    troubleshooting: [
      'Check the active diagnostic session matches this service\'s requirement',
      'Verify all documented preconditions (engine state, speed, voltage) are met',
      'Confirm the required security access level is unlocked',
      'Check no conflicting operation is already running',
    ],
    services: ['Common across most services, especially 0x10, 0x11, 0x27, 0x28, 0x2E, 0x31, 0x34–0x37, 0x85'],
  },
  0x24: {
    name: 'requestSequenceError', label: 'Request Sequence Error', category: 'Request',
    meaning: 'This request is out of order — a required prior step was skipped.',
    description: 'The service requires requests to arrive in a specific order (e.g. seed before key, download-start before transfer data), and this request arrived before that order was satisfied.',
    causes: [
      'Sending a key (SecurityAccess 0x27) without first requesting a seed',
      'TransferData sent before RequestDownload/RequestUpload',
      'RoutineControl stop/results requested before start',
    ],
    troubleshooting: [
      'Verify the required request sequence for this service (check its state machine)',
      'Confirm no steps were skipped or sent out of order',
      'Reset the diagnostic sequence and restart from the first required request',
    ],
    services: ['0x27 Security Access', '0x34/0x35/0x36/0x37 Upload/Download/Transfer Data/Transfer Exit', '0x31 Routine Control'],
  },
  0x25: {
    name: 'noResponseFromSubnetComponent', label: 'No Response From Subnet Component', category: 'Condition',
    meaning: 'A gateway ECU forwarded the request to a component on a subnet, but got no response.',
    description: 'The receiving ECU acts as a gateway to the actual target (e.g. a component behind a sub-bus), and that target failed to respond within the expected time.',
    causes: [
      'Target ECU on a subnet (e.g. sub-bus behind a gateway) is not responding',
      'Subnet bus fault, or the target node is powered down',
      'Gateway routing/addressing misconfiguration',
    ],
    troubleshooting: [
      'Check the target subnet ECU is powered and its bus is healthy',
      'Verify gateway routing configuration for the target address',
      'Confirm the subnet ECU actually implements the requested service',
    ],
    services: ['Typically returned by gateway ECUs forwarding a request to a subnet node'],
  },
  0x26: {
    name: 'failurePreventsExecutionOfRequestedAction', label: 'Failure Prevents Execution Of Requested Action', category: 'Condition',
    meaning: 'A detected fault prevents the requested action from being carried out.',
    description: 'The ECU has detected a fault (hardware, software, or vehicle-level) that specifically blocks execution of this request, distinct from a general precondition failure.',
    causes: [
      'An active DTC/fault condition blocks the requested operation',
      'A hardware fault detected makes the action unsafe or impossible',
      'Internal self-test failure',
    ],
    troubleshooting: [
      'Check for active DTCs that could block this operation',
      'Investigate the underlying hardware/software fault before retrying',
      'Clear the blocking fault first if it is safe and appropriate to do so',
    ],
    services: ['Commonly 0x31 Routine Control, 0x2E Write Data By Identifier, 0x11 ECU Reset'],
  },
  0x31: {
    name: 'requestOutOfRange', label: 'Request Out Of Range', category: 'Request',
    meaning: 'The requested parameter or operation is outside the ECU\'s permitted range.',
    description: 'The request is correctly formatted, but a parameter within it — a DID, RID, sub-function value, memory address, or similar — is not one this ECU supports or allows in its current configuration.',
    causes: [
      'Invalid or unsupported DID/RID',
      'Unsupported parameter value',
      'Parameter outside the configured/allowed range',
      'Incorrect ECU state for the requested memory address/range',
    ],
    troubleshooting: [
      'Verify the request format',
      'Check DID/RID validity against the ECU\'s spec',
      'Check the active diagnostic session',
      'Check ECU preconditions',
      'Verify against the OEM diagnostic specification',
    ],
    services: ['0x22/0x2E Data-by-identifier services', '0x31 Routine Control', '0x2F Input Output Control By Identifier', '0x14/0x19 DTC services'],
  },
  0x33: {
    name: 'securityAccessDenied', label: 'Security Access Denied', category: 'Security',
    meaning: 'This operation requires an unlocked security level, and the session is currently locked.',
    description: 'The requested service or resource is protected, and the SecurityAccess (0x27) handshake has not been successfully completed at the level this operation requires.',
    causes: [
      'SecurityAccess (0x27) has not been successfully completed at the required level',
      'Wrong security level unlocked for this operation',
      'Security unlock timed out or was cleared by a session change/ECU reset',
    ],
    troubleshooting: [
      'Perform the SecurityAccess (0x27) seed/key exchange at the correct level first',
      'Confirm which security level this service/DID/routine requires',
      'Check whether a session change or ECU reset cleared the unlock state',
    ],
    services: ['0x27-gated services, e.g. 0x2E, 0x31, 0x34–0x37, 0x2F, 0x2C'],
  },
  0x34: {
    name: 'authenticationRequired', label: 'Authentication Required', category: 'Authentication',
    meaning: 'The requested action requires the Authentication service (0x29) to be completed first.',
    description: 'Introduced alongside the Authentication service in ISO 14229-1:2020, this NRC indicates the request needs a successful 0x29 Authentication handshake that has not yet happened.',
    causes: [
      'Authentication (0x29) handshake not yet performed for this ECU',
      'Authentication session expired or was reset',
    ],
    troubleshooting: [
      'Perform the required Authentication (0x29) role/certificate exchange first',
      'Check whether a prior authentication state was cleared by a session change or reset',
    ],
    services: ['0x29 Authentication and any service that depends on it'],
  },
  0x35: {
    name: 'invalidKey', label: 'Invalid Key', category: 'Security',
    meaning: 'The key sent does not match the key computed from the seed for this security level.',
    description: 'The tester responded to a SecurityAccess seed with a key value that the ECU\'s own computation does not match, so the unlock attempt is rejected.',
    causes: [
      'Wrong algorithm or secret parameter used to compute the key',
      'Key computed from a stale/expired seed',
      'Tooling bug in the key-generation implementation',
    ],
    troubleshooting: [
      'Verify the key algorithm and parameters against the OEM security document',
      'Confirm the key was computed from the most recently issued seed',
      'Re-request a seed and recompute the key',
    ],
    services: ['0x27 Security Access'],
  },
  0x36: {
    name: 'exceedNumberOfAttempts', label: 'Exceed Number Of Attempts', category: 'Security',
    meaning: 'Too many invalid keys were sent in a row, triggering a lockout.',
    description: 'The ECU limits consecutive failed SecurityAccess attempts as a brute-force defense; once that limit is reached, further attempts are rejected until a timed lockout expires.',
    causes: [
      'Repeated incorrect key attempts (brute-force protection triggered)',
      'Automated tooling retried a wrong key/algorithm without pausing',
    ],
    troubleshooting: [
      'Wait for the ECU\'s lockout delay to expire before retrying',
      'Double-check the key algorithm before the next attempt',
      'Check that tooling isn\'t auto-retrying a known-bad key',
    ],
    services: ['0x27 Security Access'],
  },
  0x37: {
    name: 'requiredTimeDelayNotExpired', label: 'Required Time Delay Not Expired', category: 'Security',
    meaning: 'A Security Access lockout delay is still active.',
    description: 'A SecurityAccess attempt was made while the ECU is still inside the mandatory delay window that followed a previous exceedNumberOfAttempts lockout.',
    causes: [
      'A SecurityAccess attempt was made while still inside the lockout window from a previous failure',
    ],
    troubleshooting: [
      'Wait for the documented delay time to fully elapse',
      'Confirm the ECU\'s exact lockout duration in its diagnostic spec',
      'Avoid sending a new seed/key request until the delay has passed',
    ],
    services: ['0x27 Security Access'],
  },
  0x38: {
    name: 'secureDataTransmissionRequired', label: 'Secure Data Transmission Required', category: 'Security',
    meaning: 'This request must be sent via Secured Data Transmission (0x84); it was received unsecured.',
    description: 'The service being requested is configured to require the SecuredDataTransmission (0x84) wrapper, and the request arrived as plain diagnostic data instead.',
    causes: ['Request sent as plain diagnostic data instead of being wrapped by SecuredDataTransmission (0x84)'],
    troubleshooting: [
      'Wrap the request using service 0x84 Secured Data Transmission as required',
      'Confirm which services on this ECU require secured transmission',
    ],
    services: ['0x84 Secured Data Transmission and any service it protects'],
  },
  0x39: {
    name: 'secureDataTransmissionNotAllowed', label: 'Secure Data Transmission Not Allowed', category: 'Security',
    meaning: 'Secured Data Transmission was used, but is not allowed for this particular request.',
    description: 'The inverse of 0x38 — the request was wrapped in SecuredDataTransmission (0x84), but the target service does not accept or require it in this context.',
    causes: ['SecuredDataTransmission (0x84) wrapper used for a service that does not accept it in this context'],
    troubleshooting: [
      'Check whether this specific request should be sent secured or unsecured',
      'Verify against the ECU\'s security architecture documentation',
    ],
    services: ['0x84 Secured Data Transmission'],
  },
  0x3A: {
    name: 'secureDataVerificationFailed', label: 'Secure Data Verification Failed', category: 'Security',
    meaning: 'The secured data\'s integrity/authenticity check failed.',
    description: 'A SecuredDataTransmission (0x84) payload was received, but its cryptographic verification (e.g. signature or MAC check) failed.',
    causes: [
      'Corrupted or tampered secured payload',
      'Wrong keys/certificates used to sign or verify the secured data',
      'Clock/counter (freshness) mismatch',
    ],
    troubleshooting: [
      'Verify the signing keys/certificates match what the ECU expects',
      'Check for data corruption in transit',
      'Confirm freshness/counter values are in sync',
    ],
    services: ['0x84 Secured Data Transmission'],
  },
  0x50: {
    name: 'certificateVerificationFailedInvalidTimePeriod', label: 'Certificate Verification Failed — Invalid Time Period', category: 'Authentication',
    meaning: 'The certificate is not valid at the current time (expired or not yet valid).',
    description: 'Part of the Authentication (0x29) service introduced in ISO 14229-1:2020 — the presented certificate\'s validity window does not cover the ECU\'s current time.',
    causes: ['Certificate has expired', 'Certificate\'s validity start date is in the future', 'ECU real-time clock is incorrect'],
    troubleshooting: [
      'Check the certificate validity period against the ECU\'s current time',
      'Verify the ECU\'s real-time clock is correctly set',
      'Reissue/renew the certificate if it has expired',
    ],
    services: ['0x29 Authentication'],
  },
  0x51: {
    name: 'certificateVerificationFailedInvalidSignature', label: 'Certificate Verification Failed — Invalid Signature', category: 'Authentication',
    meaning: 'The certificate\'s digital signature could not be verified.',
    description: 'The certificate presented during Authentication (0x29) failed cryptographic signature verification.',
    causes: ['Certificate signed with an untrusted or wrong key', 'Certificate data corrupted in transit', 'Wrong root/CA certificate installed on the ECU'],
    troubleshooting: [
      'Verify the signing chain and CA certificate installed on the ECU',
      'Check the certificate wasn\'t corrupted or modified',
      'Confirm the correct key pair was used to sign the certificate',
    ],
    services: ['0x29 Authentication'],
  },
  0x52: {
    name: 'certificateVerificationFailedInvalidChainOfTrust', label: 'Certificate Verification Failed — Invalid Chain Of Trust', category: 'Authentication',
    meaning: 'The certificate chain does not lead back to a root the ECU trusts.',
    description: 'The certificate chain presented during Authentication (0x29) could not be traced to a Certificate Authority the ECU recognizes.',
    causes: ['Missing intermediate certificate in the chain', 'ECU does not have the required root CA installed', 'Wrong certificate chain presented'],
    troubleshooting: [
      'Verify the full certificate chain is presented, including intermediates',
      'Confirm the ECU trusts the root CA used',
      'Check the certificate provisioning process',
    ],
    services: ['0x29 Authentication'],
  },
  0x53: {
    name: 'certificateVerificationFailedInvalidType', label: 'Certificate Verification Failed — Invalid Type', category: 'Authentication',
    meaning: 'The certificate type presented doesn\'t match what this operation expects.',
    description: 'The certificate\'s role or usage extension does not match what the requested Authentication (0x29) step requires (e.g. a device certificate used where a CA certificate is expected).',
    causes: ['Wrong certificate used for this authentication step', 'Certificate purpose/usage extension mismatch'],
    troubleshooting: [
      'Confirm the correct certificate type/role for this authentication step',
      'Check the certificate\'s extended key usage fields',
    ],
    services: ['0x29 Authentication'],
  },
  0x54: {
    name: 'certificateVerificationFailedInvalidFormat', label: 'Certificate Verification Failed — Invalid Format', category: 'Authentication',
    meaning: 'The certificate could not be parsed — its encoding is invalid.',
    description: 'The bytes presented as a certificate during Authentication (0x29) do not decode as a valid certificate structure.',
    causes: ['Certificate encoding corrupted or truncated', 'Wrong certificate format sent (e.g. DER vs. PEM mismatch)'],
    troubleshooting: [
      'Verify the certificate encoding matches what the ECU expects',
      'Check for truncation or corruption during transfer',
    ],
    services: ['0x29 Authentication'],
  },
  0x55: {
    name: 'certificateVerificationFailedInvalidContent', label: 'Certificate Verification Failed — Invalid Content', category: 'Authentication',
    meaning: 'The certificate\'s content fails an ECU-specific check.',
    description: 'The certificate parses correctly, but a required field, extension, or content policy the ECU enforces is missing or invalid.',
    causes: ['Required certificate fields/extensions missing or invalid', 'Certificate content does not match expected policy'],
    troubleshooting: [
      'Check the certificate against the OEM\'s required content/extensions',
      'Confirm the certificate issuance process follows the ECU\'s policy',
    ],
    services: ['0x29 Authentication'],
  },
  0x56: {
    name: 'certificateVerificationFailedInvalidScope', label: 'Certificate Verification Failed — Invalid Scope', category: 'Authentication',
    meaning: 'The certificate is valid, but not authorized for this specific request.',
    description: 'The certificate itself verifies correctly, but its authorized scope (e.g. which ECU or operation it covers) does not include the current request.',
    causes: ['Certificate scoped to a different ECU/operation than requested', 'Requested action exceeds what this certificate authorizes'],
    troubleshooting: [
      'Confirm the certificate\'s authorized scope covers this request',
      'Use or obtain a certificate with the correct scope',
    ],
    services: ['0x29 Authentication'],
  },
  0x57: {
    name: 'certificateVerificationFailedInvalidCertificate', label: 'Certificate Verification Failed — Invalid Certificate (Revoked)', category: 'Authentication',
    meaning: 'The certificate has been revoked and is no longer trusted.',
    description: 'The certificate is otherwise well-formed and signed, but has since been revoked (e.g. following key compromise or tool decommissioning).',
    causes: ['Certificate was revoked (e.g. compromised key, decommissioned tool)', 'Using an outdated/cached certificate'],
    troubleshooting: [
      'Obtain a current, non-revoked certificate',
      'Check the certificate revocation list (CRL)/OCSP status',
    ],
    services: ['0x29 Authentication'],
  },
  0x58: {
    name: 'ownershipVerificationFailed', label: 'Ownership Verification Failed', category: 'Authentication',
    meaning: 'Proof of ownership of the certificate/key could not be verified.',
    description: 'The Authentication (0x29) challenge-response step meant to prove possession of the private key matching the presented certificate did not verify.',
    causes: ['Wrong private key used to answer the ownership challenge', 'Challenge/response computation error'],
    troubleshooting: [
      'Verify the private key corresponds to the presented certificate',
      'Check the challenge-response algorithm implementation',
    ],
    services: ['0x29 Authentication'],
  },
  0x59: {
    name: 'challengeCalculationFailed', label: 'Challenge Calculation Failed', category: 'Authentication',
    meaning: 'The ECU could not generate the authentication challenge.',
    description: 'An internal failure prevented the ECU from producing the challenge value needed for the Authentication (0x29) handshake.',
    causes: ['Internal ECU fault during challenge generation', 'Resource/entropy source unavailable'],
    troubleshooting: [
      'Retry the authentication request',
      'Check ECU logs for an internal fault during challenge generation',
    ],
    services: ['0x29 Authentication'],
  },
  0x5A: {
    name: 'settingAccessRightsFailed', label: 'Setting Access Rights Failed', category: 'Authentication',
    meaning: 'The ECU could not apply the access rights tied to this authentication.',
    description: 'Authentication itself succeeded, but the ECU failed to apply the resulting role/access rights internally.',
    causes: ['Internal error applying the role/access rights after successful authentication', 'Access-rights configuration missing or invalid on the ECU'],
    troubleshooting: [
      'Check the ECU-side access rights/role configuration',
      'Retry the authentication sequence',
    ],
    services: ['0x29 Authentication'],
  },
  0x5B: {
    name: 'sessionKeyCreationOrDerivationFailed', label: 'Session Key Creation Or Derivation Failed', category: 'Authentication',
    meaning: 'The session key needed for secure communication could not be created.',
    description: 'The key derivation step following successful Authentication (0x29) failed, so no secure session key was produced.',
    causes: ['Key derivation function failure (bad inputs/parameters)', 'Internal cryptographic fault'],
    troubleshooting: [
      'Verify key derivation inputs and algorithm parameters',
      'Retry the authentication/session-key exchange',
    ],
    services: ['0x29 Authentication'],
  },
  0x5C: {
    name: 'configurationDataUsageFailed', label: 'Configuration Data Usage Failed', category: 'Authentication',
    meaning: 'Configuration data needed for authentication could not be used.',
    description: 'The ECU has authentication-related configuration data (e.g. trust anchors, policy tables) that could not be loaded or applied correctly.',
    causes: ['Corrupted or missing authentication configuration data on the ECU', 'Version mismatch between configuration data and ECU software'],
    troubleshooting: [
      'Verify authentication configuration data is present and valid',
      'Check for a version mismatch after an ECU software update',
    ],
    services: ['0x29 Authentication'],
  },
  0x5D: {
    name: 'deAuthenticationFailed', label: 'De-Authentication Failed', category: 'Authentication',
    meaning: 'The ECU could not complete a de-authentication (logout) request.',
    description: 'A request to end the authenticated session failed to complete cleanly on the ECU side.',
    causes: ['Internal error tearing down the authenticated session', 'Session already in an inconsistent state'],
    troubleshooting: [
      'Retry the de-authentication request',
      'Power-cycle or reset the ECU if the authenticated state appears stuck',
    ],
    services: ['0x29 Authentication'],
  },
  0x70: {
    name: 'uploadDownloadNotAccepted', label: 'Upload/Download Not Accepted', category: 'Upload/Download',
    meaning: 'The ECU rejects starting the requested upload/download operation.',
    description: 'The RequestDownload/RequestUpload request itself is rejected — the transfer never starts.',
    causes: [
      'Wrong preconditions for flashing/memory transfer (session, security, voltage)',
      'Requested memory region not valid for upload/download',
      'Another transfer already in progress',
    ],
    troubleshooting: [
      'Confirm Programming session and required security access are active',
      'Verify the memory address/size requested is valid for this ECU',
      'Check that no other transfer is already active',
    ],
    services: ['0x34 Request Download', '0x35 Request Upload'],
  },
  0x71: {
    name: 'transferDataSuspended', label: 'Transfer Data Suspended', category: 'Upload/Download',
    meaning: 'An ongoing data transfer was suspended before completion.',
    description: 'A transfer that was previously accepted and in progress has been stopped before all data was transferred.',
    causes: [
      'Communication interruption during TransferData',
      'ECU-side fault paused the transfer',
      'Tester aborted or timed out mid-transfer',
    ],
    troubleshooting: [
      'Restart the transfer sequence from RequestDownload/RequestUpload',
      'Check bus stability during large transfers',
      'Verify tester timing/timeout settings',
    ],
    services: ['0x36 Transfer Data'],
  },
  0x72: {
    name: 'generalProgrammingFailure', label: 'General Programming Failure', category: 'Upload/Download',
    meaning: 'A general failure occurred while erasing or programming non-volatile memory.',
    description: 'The ECU attempted to erase or write flash/EEPROM as part of the transfer and the operation failed at the hardware/firmware level.',
    causes: [
      'Flash erase/write failure (hardware fault, voltage sag)',
      'Corrupted data block received',
      'Programming voltage/power outside the required window',
    ],
    troubleshooting: [
      'Verify power supply is stable and within the ECU\'s programming voltage window',
      'Re-check the data block for corruption/checksum errors',
      'Retry the erase/program sequence',
    ],
    services: ['0x34–0x37 flash/download sequence', '0x31 Routine Control (erase memory routine)'],
  },
  0x73: {
    name: 'wrongBlockSequenceCounter', label: 'Wrong Block Sequence Counter', category: 'Upload/Download',
    meaning: 'The TransferData block sequence counter is out of the expected order.',
    description: 'Each TransferData request carries an incrementing block sequence counter; this response means the counter received doesn\'t match what the ECU expected next.',
    causes: [
      'A TransferData block was skipped, duplicated, or resent out of order',
      'Block sequence counter wrapped (0xFF → 0x00) and was mishandled by tooling',
    ],
    troubleshooting: [
      'Ensure TransferData blocks are sent in strict incrementing order starting at 1',
      'Check tooling correctly wraps the counter from 0xFF back to 0x00',
      'Restart the transfer if the sequence has desynced',
    ],
    services: ['0x36 Transfer Data'],
  },
  0x78: {
    name: 'requestCorrectlyReceived-ResponsePending', label: 'Request Correctly Received — Response Pending', category: 'Response Pending',
    meaning: 'The ECU accepted the request but needs more time to prepare the actual response.',
    description: 'This is not an error — it tells the tester the request was valid and is being processed; the final positive or negative response (or another 0x78) will follow.',
    causes: [
      'Long-running internal operation (e.g. erasing flash, running a routine, computing a result)',
      'ECU intentionally extending the P2 timing window',
    ],
    troubleshooting: [
      'Keep waiting for the final response — this is not itself an error',
      'Extend the tester\'s P2* timeout to accommodate repeated 0x78 responses',
      'If it never resolves, check the ECU for a stuck internal operation',
    ],
    services: ['Any service with an operation that can exceed the default P2 timing, notably 0x31, 0x34–0x37, 0x11'],
  },
  0x7E: {
    name: 'subFunctionNotSupportedInActiveSession', label: 'Sub-Function Not Supported In Active Session', category: 'Session',
    meaning: 'This sub-function isn\'t available in the currently active diagnostic session.',
    description: 'Unlike 0x12, the sub-function does exist on this ECU — it just isn\'t enabled in the session that is active right now.',
    causes: [
      'Sub-function only valid in Extended/Programming session, but Default session is active',
      'Session changed unexpectedly (e.g. S3 timeout) before the request',
    ],
    troubleshooting: [
      'Switch to the diagnostic session that supports this sub-function',
      'Check for an S3 session timeout dropping back to Default session',
      'Send Tester Present (0x3E) to keep a non-default session alive',
    ],
    services: ['Any sub-function-based service, e.g. 0x10, 0x11, 0x28, 0x3E, 0x85'],
  },
  0x7F: {
    name: 'serviceNotSupportedInActiveSession', label: 'Service Not Supported In Active Session', category: 'Session',
    meaning: 'This service isn\'t available in the currently active diagnostic session.',
    description: 'Unlike 0x11, the service does exist on this ECU — it just isn\'t enabled in the session that is active right now.',
    causes: [
      'Service only available in Extended/Programming session',
      'Session dropped back to Default (e.g. S3 timeout) before the request was sent',
    ],
    troubleshooting: [
      'Switch to the diagnostic session that supports this service',
      'Send Tester Present (0x3E) periodically to hold the session',
      'Re-check the session-to-service applicability table in the ECU spec',
    ],
    services: ['Any service restricted to a non-default session, e.g. 0x2E, 0x31, 0x34–0x37'],
  },
  0x81: {
    name: 'rpmTooHigh', label: 'RPM Too High', category: 'Vehicle Condition',
    meaning: 'Engine/motor RPM is above the allowed threshold for this operation.',
    description: 'A vehicle-condition precondition tied to engine/motor speed is not met — the requested action is only permitted below a certain RPM.',
    causes: ['Requested action only permitted at idle/low RPM', 'Engine running at a higher RPM than the operation allows'],
    troubleshooting: [
      'Reduce engine/motor speed (or bring it to idle) before retrying',
      'Check the documented RPM precondition for this specific service/routine',
    ],
    services: ['Typically 0x31 Routine Control, 0x2E Write Data By Identifier'],
  },
  0x82: {
    name: 'rpmTooLow', label: 'RPM Too Low', category: 'Vehicle Condition',
    meaning: 'Engine/motor RPM is below the allowed threshold for this operation.',
    description: 'The requested action requires a minimum engine/motor speed that isn\'t currently met.',
    causes: ['Requested action requires a minimum RPM not currently met', 'Engine stalled or not running when a minimum-RPM precondition applies'],
    troubleshooting: [
      'Start the engine/bring RPM above the required minimum before retrying',
      'Check the documented RPM precondition for this service/routine',
    ],
    services: ['Typically 0x31 Routine Control'],
  },
  0x83: {
    name: 'engineIsRunning', label: 'Engine Is Running', category: 'Vehicle Condition',
    meaning: 'This action is only allowed while the engine is stopped.',
    description: 'The requested action must be performed with the engine off — often a safety interlock for actuator tests or flashing.',
    causes: ['Operation requires engine off for safety (e.g. certain actuator tests, flashing)'],
    troubleshooting: [
      'Turn the engine off and retry',
      'Confirm the exact engine-state precondition documented for this service',
    ],
    services: ['Typically 0x31 Routine Control, 0x2F Input Output Control By Identifier'],
  },
  0x84: {
    name: 'engineIsNotRunning', label: 'Engine Is Not Running', category: 'Vehicle Condition',
    meaning: 'This action requires the engine to be running.',
    description: 'The requested action needs the engine running — often for a live actuator or sensor test.',
    causes: ['Operation requires a running engine (e.g. live actuator/sensor test)'],
    troubleshooting: [
      'Start the engine and retry',
      'Confirm the exact engine-state precondition documented for this service',
    ],
    services: ['Typically 0x31 Routine Control, 0x2F Input Output Control By Identifier'],
  },
  0x85: {
    name: 'engineRunTimeTooLow', label: 'Engine Run Time Too Low', category: 'Vehicle Condition',
    meaning: 'The engine hasn\'t been running long enough for this operation.',
    description: 'The requested action needs the engine to have reached a stable/warmed-up state, which requires a minimum run time.',
    causes: ['Operation requires a minimum run time to reach a stable/warm state'],
    troubleshooting: [
      'Let the engine run for the required warm-up period before retrying',
      'Check the documented minimum run-time precondition',
    ],
    services: ['Typically 0x31 Routine Control'],
  },
  0x86: {
    name: 'temperatureTooHigh', label: 'Temperature Too High', category: 'Vehicle Condition',
    meaning: 'A monitored temperature is above the allowed threshold.',
    description: 'A monitored engine, battery, or component temperature exceeds the safe operating window for this action.',
    causes: ['Component/engine/battery temperature above the safe operating window for this action'],
    troubleshooting: [
      'Allow the component to cool before retrying',
      'Check which temperature sensor/threshold applies to this specific service',
    ],
    services: ['Typically 0x31 Routine Control, 0x2E Write Data By Identifier'],
  },
  0x87: {
    name: 'temperatureTooLow', label: 'Temperature Too Low', category: 'Vehicle Condition',
    meaning: 'A monitored temperature is below the allowed threshold.',
    description: 'A monitored engine, battery, or component temperature is below the required operating window for this action.',
    causes: ['Component/engine/battery temperature below the required operating window for this action'],
    troubleshooting: [
      'Allow the component to warm up before retrying',
      'Check which temperature sensor/threshold applies to this specific service',
    ],
    services: ['Typically 0x31 Routine Control'],
  },
  0x88: {
    name: 'vehicleSpeedTooHigh', label: 'Vehicle Speed Too High', category: 'Vehicle Condition',
    meaning: 'Vehicle speed is above the allowed threshold — usually a safety interlock.',
    description: 'The requested action is only permitted at low or zero vehicle speed.',
    causes: ['Operation only permitted at low/zero speed for safety'],
    troubleshooting: [
      'Bring the vehicle to a stop or below the required speed threshold',
      'Confirm this is a stationary-only operation',
    ],
    services: ['Typically 0x2F Input Output Control By Identifier, 0x31 Routine Control'],
  },
  0x89: {
    name: 'vehicleSpeedTooLow', label: 'Vehicle Speed Too Low', category: 'Vehicle Condition',
    meaning: 'Vehicle speed is below the allowed threshold for this operation.',
    description: 'The requested action requires the vehicle to be in motion above a minimum speed.',
    causes: ['Operation requires the vehicle to be moving above a minimum speed'],
    troubleshooting: ['Drive the vehicle above the required minimum speed before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x8A: {
    name: 'throttlePedalTooHigh', label: 'Throttle/Pedal Too High', category: 'Vehicle Condition',
    meaning: 'Throttle/accelerator pedal position is above the allowed threshold.',
    description: 'The requested action requires the accelerator pedal to be released or near closed throttle.',
    causes: ['Operation requires the pedal to be released/near closed throttle'],
    troubleshooting: ['Release the accelerator pedal before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x8B: {
    name: 'throttlePedalTooLow', label: 'Throttle/Pedal Too Low', category: 'Vehicle Condition',
    meaning: 'Throttle/accelerator pedal position is below the allowed threshold.',
    description: 'The requested action requires a minimum throttle/pedal input that isn\'t currently present.',
    causes: ['Operation requires a minimum throttle/pedal input not currently present'],
    troubleshooting: ['Apply the required minimum throttle/pedal input before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x8C: {
    name: 'transmissionRangeNotInNeutral', label: 'Transmission Range Not In Neutral', category: 'Vehicle Condition',
    meaning: 'The transmission must be in Neutral for this operation.',
    description: 'The requested action requires the transmission range to be Neutral, and it currently is not.',
    causes: ['Transmission in a drive/reverse/park range when Neutral is required'],
    troubleshooting: ['Shift the transmission to Neutral before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x8D: {
    name: 'transmissionRangeNotInGear', label: 'Transmission Range Not In Gear', category: 'Vehicle Condition',
    meaning: 'The transmission must be in gear (Drive/Reverse) for this operation.',
    description: 'The requested action requires the transmission range to be Drive or Reverse, and it currently is not.',
    causes: ['Transmission in Park/Neutral when a driving gear is required'],
    troubleshooting: ['Shift the transmission into gear before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x8F: {
    name: 'brakeSwitchOrSwitchesNotClosed', label: 'Brake Switch(es) Not Closed', category: 'Vehicle Condition',
    meaning: 'The brake pedal switch(es) must be pressed (closed) for this operation.',
    description: 'A safety interlock requiring the brake pedal to be pressed is not currently satisfied.',
    causes: ['Brake pedal not pressed when the operation requires it as a safety interlock'],
    troubleshooting: ['Press and hold the brake pedal before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x90: {
    name: 'shifterLeverNotInPark', label: 'Shifter Lever Not In Park', category: 'Vehicle Condition',
    meaning: 'The gear shifter must be in Park for this operation.',
    description: 'The requested action (often flashing or a specific routine) requires the shifter to be in Park.',
    causes: ['Shifter in a non-Park position when Park is required (e.g. for flashing or certain routines)'],
    troubleshooting: ['Move the shifter to Park before retrying'],
    services: ['Typically 0x31 Routine Control'],
  },
  0x91: {
    name: 'torqueConverterClutchLocked', label: 'Torque Converter Clutch Locked', category: 'Vehicle Condition',
    meaning: 'The torque converter clutch lock state doesn\'t match what this operation requires.',
    description: 'The requested action has a precondition on the torque converter clutch (TCC) lock state that isn\'t currently satisfied.',
    causes: ['Torque converter clutch (TCC) lock state doesn\'t match what this operation requires'],
    troubleshooting: [
      'Check the ECU\'s documented required TCC state for this operation',
      'Change driving conditions to shift TCC lock state if applicable',
    ],
    services: ['Typically 0x31 Routine Control'],
  },
  0x92: {
    name: 'voltageTooHigh', label: 'Voltage Too High', category: 'Vehicle Condition',
    meaning: 'Supply/battery voltage is above the allowed threshold — commonly during programming.',
    description: 'The requested action (frequently ECU flashing) requires supply voltage to stay within a defined window, and it is currently too high.',
    causes: ['Battery/charging voltage above the safe window for flashing or the requested action'],
    troubleshooting: [
      'Check the charging system/voltage regulator for over-voltage',
      'Wait for voltage to settle into the required window before retrying',
    ],
    services: ['Typically 0x34–0x37 flash sequence, 0x31 Routine Control'],
  },
  0x93: {
    name: 'voltageTooLow', label: 'Voltage Too Low', category: 'Vehicle Condition',
    meaning: 'Supply/battery voltage is below the allowed threshold — commonly during programming.',
    description: 'The requested action (frequently ECU flashing) requires supply voltage to stay within a defined window, and it is currently too low.',
    causes: ['Weak/discharged battery voltage below the safe window for flashing or the requested action'],
    troubleshooting: [
      'Connect a stable bench power supply/battery charger before retrying',
      'Check battery health and the charging system',
    ],
    services: ['Typically 0x34–0x37 flash sequence, 0x31 Routine Control'],
  },
  0x94: {
    name: 'resourceTemporarilyNotAvailable', label: 'Resource Temporarily Not Available', category: 'Condition',
    meaning: 'A resource the ECU needs for this request is temporarily unavailable.',
    description: 'Introduced in ISO 14229-1:2020 — an internal resource (memory buffer, task slot, etc.) the ECU needs to process this request is currently in use elsewhere.',
    causes: ['Internal ECU resource (RAM buffer, task slot) is in use by another operation', 'Transient resource contention'],
    troubleshooting: [
      'Retry the request after a short delay',
      'Check for concurrent diagnostic operations competing for the same resource',
    ],
    services: ['Any service'],
  },
};

// Reserved / manufacturer-specific ranges, shown as grouped rows in the reference table
// and used to classify a decoded NRC that falls inside one of them. Per ISO 14229-1,
// these ranges are either reserved for a future edition of the standard, or explicitly
// left open for vehicle-manufacturer-specific use — this tool does not invent meanings
// for codes inside them.
export const NRC_RANGES = [
  { start: 0x00, end: 0x0f, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x15, end: 0x20, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x23, end: 0x23, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x27, end: 0x30, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x32, end: 0x32, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x3b, end: 0x4f, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1 for future definition. Not defined as a negative response code.' },
  { start: 0x5e, end: 0x6f, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x74, end: 0x77, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x79, end: 0x7d, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x80, end: 0x80, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x8e, end: 0x8e, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
  { start: 0x95, end: 0xef, label: 'ISOSAEReserved / conditionsNotCorrect extension', category: 'Reserved', description: 'Reserved by ISO 14229-1 for future definition. Some ECU implementations treat part of this band as manufacturer-specific — check the applicable OEM diagnostic specification.' },
  { start: 0xf0, end: 0xfe, label: 'vehicleManufacturerSpecificConditionsNotCorrect', category: 'Manufacturer Specific', description: 'Reserved by ISO 14229-1 for vehicle-manufacturer-specific use. The exact meaning is defined by the OEM/ECU supplier, not by ISO 14229-1 itself.' },
  { start: 0xff, end: 0xff, label: 'ISOSAEReserved', category: 'Reserved', description: 'Reserved by ISO 14229-1. Not defined as a negative response code.' },
];

const findRange = (code) => NRC_RANGES.find((r) => code >= r.start && code <= r.end) || null;

// Returns a normalized lookup result for any single-byte code (0-255): either a
// standardized entry from NRC_DATABASE, or a synthesized entry for a reserved/OEM
// range, always with the same shape so callers don't need to branch on reserved-ness.
export const getNrcEntry = (code) => {
  const known = NRC_DATABASE[code];
  if (known) {
    return { code, hex: `0x${hex2(code)}`, decimal: code, reserved: false, ...known };
  }
  const range = findRange(code);
  if (range) {
    return {
      code,
      hex: `0x${hex2(code)}`,
      decimal: code,
      reserved: true,
      name: range.label,
      label: range.label,
      category: range.category,
      meaning: range.category === 'Manufacturer Specific'
        ? 'This code is reserved for vehicle-manufacturer-specific use — its meaning is defined by the OEM/ECU supplier.'
        : 'This code is reserved by ISO 14229-1 and has no standardized meaning defined.',
      description: range.description,
      causes: [],
      troubleshooting: ['Consult the target ECU\'s own OEM diagnostic specification for this code\'s meaning in this context.'],
      services: [],
      rangeLabel: `0x${hex2(range.start)}–0x${hex2(range.end)}`,
    };
  }
  // Should not happen for a valid single byte (0-255) given the ranges above fully
  // cover 0x00-0xFF, but keep a safe fallback rather than throwing.
  return {
    code, hex: `0x${hex2(code)}`, decimal: code, reserved: true,
    name: 'Unknown', label: 'Unknown', category: 'Reserved',
    meaning: 'This code is not in the reference table.', description: '',
    causes: [], troubleshooting: [], services: [],
  };
};

// Flattened rows for the searchable reference table: one row per standardized NRC, plus
// one row per reserved/manufacturer-specific range, sorted by starting code.
export const NRC_TABLE_ROWS = [
  ...Object.entries(NRC_DATABASE).map(([code, v]) => ({
    key: `code-${code}`,
    hex: `0x${hex2(Number(code))}`,
    decimal: Number(code),
    name: v.label,
    category: v.category,
    description: v.meaning,
    reserved: false,
    searchText: `${hex2(Number(code))} ${code} ${v.name} ${v.label} ${v.category} ${v.meaning} ${v.description}`.toLowerCase(),
  })),
  ...NRC_RANGES.map((r) => ({
    key: `range-${r.start}-${r.end}`,
    hex: r.start === r.end ? `0x${hex2(r.start)}` : `0x${hex2(r.start)}–0x${hex2(r.end)}`,
    decimal: r.start === r.end ? r.start : `${r.start}–${r.end}`,
    name: r.label,
    category: r.category,
    description: r.description,
    reserved: true,
    searchText: `${r.label} ${r.category} ${r.description} reserved`.toLowerCase(),
  })),
].sort((a, b) => (typeof a.decimal === 'number' ? a.decimal : a.decimal.split('–')[0]) - (typeof b.decimal === 'number' ? b.decimal : b.decimal.split('–')[0]));
